import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { SettingRow } from '@solum/sdk'
import { accessToken } from '@solum/auth'
import { Button, Spinner } from '@solum/ui'
import { useSolum } from '../data/client'
import { usePaletteScope } from '../app/paletteScope'
import { aiIdentity } from './identity'
import { ctxFor, runCopilot, type Ctx } from './brain'
import {
  addCopilotMessage, boundedCopilotHistory, consumeCopilotLaunch,
  newCopilotConversation, setCopilotBusy, setCopilotDraft,
  useCopilotConversation,
} from './conversationStore'
import s from './CopilotConversation.module.css'
import { readableError } from '@solum/sdk'

/** A mounted, same-session conversation whose context changes only deliberately. */
export function CopilotConversation({ history }: { history?: ReactNode }) {
  const state = useCopilotConversation()
  const { client, isLive } = useSolum()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (router) => router.location.pathname })
  const scope = usePaletteScope()
  const liveContext = useMemo(() => ctxFor(scope, pathname), [scope, pathname])
  const context = state.contextSnapshot ?? liveContext
  const env = import.meta.env as Record<string, string | undefined>
  const cfg = useQuery({ queryKey: ['settings', 'ai'], queryFn: () => client.describeSettings('ai') })
  const identity = aiIdentity(cfg.data as SettingRow[] | undefined)
  const composer = useRef<HTMLTextAreaElement>(null)

  const submit = async (raw: string, captured: Ctx = context) => {
    const query = raw.trim()
    if (!query || state.busy) return
    const prior = boundedCopilotHistory(state.messages)
    setCopilotDraft('')
    addCopilotMessage('user', query)
    setCopilotBusy(true)
    try {
      const answer = await runCopilot(query, captured, {
        client,
        navigate: (to) => void (navigate as unknown as (opts: { to: string }) => void)({ to }),
        isLive,
        copilotUrl: env.VITE_COPILOT_URL,
        token: (await accessToken()) ?? undefined,
        model: identity.model,
        provider: identity.provider,
        history: prior,
      })
      addCopilotMessage('assistant', answer)
    } catch (error) {
      addCopilotMessage('error', `Something went wrong: ${readableError(error)}`)
    } finally {
      setCopilotBusy(false)
    }
  }

  useEffect(() => {
    const launch = state.pendingLaunch
    if (!launch) return
    consumeCopilotLaunch(launch.id)
    composer.current?.focus()
    if (launch.query) void submit(launch.query, state.contextSnapshot ?? launch.contextSnapshot)
    // The launch id is the once-only delivery token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pendingLaunch?.id])

  return (
    <section className={s.root} aria-label="Copilot conversation" data-testid="copilot-conversation">
      <header className={s.header}>
        <div><strong>Copilot</strong><span>Working with {context.label}</span></div>
        <Button size="sm" variant="ghost" onClick={() => {
          newCopilotConversation(liveContext)
          setTimeout(() => composer.current?.focus(), 0)
        }}>New conversation</Button>
      </header>
      <div className={s.transcript} role="log" aria-live="polite" aria-label="Conversation">
        {state.messages.length === 0 && <p className={s.empty}>Ask about this page, find a record, or continue a task from the command palette.</p>}
        {state.messages.map((message) => (
          <article key={message.id} className={s.message} data-role={message.role}>
            <span>{message.role === 'user' ? 'You' : message.role === 'assistant' ? 'Copilot' : 'Could not answer'}</span>
            <p>{message.content}</p>
          </article>
        ))}
        {state.busy && <div className={s.thinking}><Spinner /> Working…</div>}
      </div>
      <form className={s.composer} onSubmit={(event) => { event.preventDefault(); void submit(state.draft) }}>
        <label htmlFor="copilot-drawer-prompt">Ask about {context.label}</label>
        <textarea ref={composer} id="copilot-drawer-prompt" value={state.draft}
          onChange={(event) => setCopilotDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              void submit(state.draft)
            }
          }} rows={3} />
        <Button type="submit" disabled={state.busy || !state.draft.trim()}>Ask</Button>
      </form>
      {history && <details className={s.history}><summary>Tool activity</summary>{history}</details>}
    </section>
  )
}
